import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent } from '@mode/shared/ui';
import * as TextFormattingHelpers from './../text-formatting-helpers';

export const BASE_FORMAT: ChartTypes.TextFormat.NumberType = {
  ...TextFormattingHelpers.getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Numeric),
};

@Component({
  standalone: true,
  selector: 'mode-number-format-pane',
  templateUrl: './number-format-pane.component.html',
  styleUrls: ['./number-format-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LabeledMenuComponent],
})
export class NumberFormatPaneComponent implements OnChanges {
  @Input({ required: true }) format!: ChartTypes.TextFormat.NumberType;
  @Output() formatChange = new EventEmitter<ChartTypes.TextFormat.NumberType>();

  readonly displayUnitMenuItems = TextFormattingHelpers.DisplayUnitMenuItems;
  readonly thousandsSeparatorMenuItems = TextFormattingHelpers.ThousandsSeparatorMenuItems;
  readonly thousandsGroupingMenuItems = TextFormattingHelpers.ThousandsGroupingMenuItems;
  readonly decimalSymbolMenuItems = TextFormattingHelpers.DecimalSeparatorMenuItems;

  selectedDisplayUnit?: TextFormattingHelpers.DisplayUnitMenuItem;
  selectedThousandsSeparator?: TextFormattingHelpers.ThousandsSeparatorMenuItem;
  selectedThousandsGrouping?: TextFormattingHelpers.ThousandsGroupingMenuItem;
  selectedDecimalSymbol?: TextFormattingHelpers.DecimalSeparatorMenuItem;

  ngOnChanges() {
    const format =
      // Mix in the BASE_FORMAT such that we always have
      // the minimum set of properties per the format type
      (this.format = {
        ...BASE_FORMAT,
        ...this.format,
      });

    this.selectedDisplayUnit = this.displayUnitMenuItems.find((opt) => format.displayUnit === opt.value);
    this.selectedThousandsSeparator = this.thousandsSeparatorMenuItems.find((opt) => format.separator === opt.value);
    this.selectedThousandsGrouping = this.thousandsGroupingMenuItems.find((opt) => format.grouping === opt.value);
    this.selectedDecimalSymbol = this.decimalSymbolMenuItems.find((opt) => format.decimalStyle === opt.value);
  }

  apply(props: Partial<ChartTypes.TextFormat.NumberType>) {
    this.format = { ...this.format, ...props };
    this.formatChange.emit(this.format);
  }

  onChangeUnit(item: TextFormattingHelpers.DisplayUnitMenuItem) {
    this.selectedDisplayUnit = item;
    this.apply({
      displayUnit: item.value,
    });
  }

  onChangeSeparator(item: TextFormattingHelpers.ThousandsSeparatorMenuItem) {
    this.selectedThousandsSeparator = item;
    this.apply({
      separator: item.value,
    });
  }

  onChangeGrouping(item: TextFormattingHelpers.ThousandsGroupingMenuItem) {
    this.selectedThousandsGrouping = item;
    this.apply({
      grouping: item.value,
    });
  }

  onChangeSymbol(item: TextFormattingHelpers.DecimalSeparatorMenuItem) {
    this.selectedDecimalSymbol = item;
    this.apply({
      decimalStyle: item.value,
    });
  }

  onChangePrecision(value: unknown) {
    const precision = TextFormattingHelpers.isValidPrecision(value) ? value : BASE_FORMAT.precision;
    this.apply({
      precision,
    });
  }
}
